import { useState } from "react";
import { useLocation } from "wouter";
import { SpotDetailMap } from "../components/map/SpotDetailMap";
import {
  DEFAULT_PARTICLE_CONFIG,
  type ParticleConfig,
} from "../components/map/UniformWindParticles";
import { MAP_STYLE_OPTIONS } from "../lib/mapConfig";
import { degreesToCompass } from "../lib/scorer";
import type { Coords } from "../lib/types";
import styles from "./ParticlesTunePage.module.css";

interface Scenario {
  label: string;
  coords: Coords;
  zoom: number;
  /** Meteorological direction (where wind is from). */
  dirDeg: number;
  /** Wind speed in m/s. */
  speedMs: number;
}

// Mock scenarios — varied locations, directions, speeds, AND zoom levels
// so parameters can be evaluated across what we expect in real data.
// Edit this array (or add new entries) to test more locations.
const SCENARIOS: Scenario[] = [
  // Same wind, different zooms — sanity-check the zoom-aware speed scaling.
  { label: "Z 9 · 8 m/s NW · Stockholm",   coords: { lat: 59.33, lon: 18.07 }, zoom: 9,  dirDeg: 315, speedMs: 8 },
  { label: "Z 11 · 8 m/s NW · Stockholm",  coords: { lat: 59.33, lon: 18.07 }, zoom: 11, dirDeg: 315, speedMs: 8 },
  { label: "Z 13 · 8 m/s NW · Stockholm",  coords: { lat: 59.33, lon: 18.07 }, zoom: 13, dirDeg: 315, speedMs: 8 },

  // Speed range at one zoom — calm → light → fresh → storm.
  { label: "Calm S · Inland lake",         coords: { lat: 60.5,  lon: 15.0 },  zoom: 11, dirDeg: 180, speedMs: 1 },
  { label: "Light N · Stockholm",          coords: { lat: 59.33, lon: 18.07 }, zoom: 11, dirDeg: 0,   speedMs: 3 },
  { label: "Moderate ESE · Gotland",       coords: { lat: 57.47, lon: 18.49 }, zoom: 11, dirDeg: 112, speedMs: 7 },
  { label: "Fresh W · Bohuslän",           coords: { lat: 58.0,  lon: 11.5 },  zoom: 11, dirDeg: 270, speedMs: 12 },
  { label: "Strong NW · Skåne",            coords: { lat: 55.95, lon: 13.55 }, zoom: 11, dirDeg: 315, speedMs: 18 },
  { label: "Storm NE · Öland",             coords: { lat: 56.74, lon: 16.63 }, zoom: 11, dirDeg: 45,  speedMs: 25 },

  // Diverse coastal/inland/island spots at varied zooms.
  { label: "Sea breeze E · Göteborg",      coords: { lat: 57.71, lon: 11.91 }, zoom: 12, dirDeg: 90,  speedMs: 6 },
  { label: "Fresh SE · Helsingborg",       coords: { lat: 56.04, lon: 12.69 }, zoom: 12, dirDeg: 135, speedMs: 10 },
  { label: "Light NW · Lake Vänern",       coords: { lat: 58.92, lon: 13.30 }, zoom: 10, dirDeg: 315, speedMs: 4 },
];

interface SliderConfig {
  key: keyof ParticleConfig;
  label: string;
  min: number;
  max: number;
  step: number;
  format?: (v: number) => string;
  hint?: string;
}

const SLIDERS: SliderConfig[] = [
  {
    key: "speedPxPerMs",
    label: "speed (px/frame per m/s)",
    min: 0.001,
    max: 0.5,
    step: 0.001,
    format: (v) => v.toFixed(3),
    hint: "Linear scaling — no zoom or latitude factor. Higher = particles travel faster.",
  },
  {
    key: "trailLengthPx",
    label: "trail length (px)",
    min: 4,
    max: 1000,
    step: 1,
    format: (v) => `${Math.round(v)} px`,
    hint: "Tail-to-head pixel length of every streak. Independent of wind speed. Each particle is drawn as a gradient stroke from upstream tail to current head every frame.",
  },
  {
    key: "tailAlpha",
    label: "tail alpha",
    min: 0,
    max: 1,
    step: 0.01,
    format: (v) => v.toFixed(2),
    hint: "Alpha at the TAIL of the gradient. 0 = comet (transparent → opaque). 1 = solid line (no taper).",
  },
  {
    key: "lineWidth",
    label: "line width (css px)",
    min: 0.5,
    max: 4,
    step: 0.1,
    format: (v) => v.toFixed(1),
    hint: "Particle stroke thickness.",
  },
  {
    key: "particleCount",
    label: "particle count",
    min: 30,
    max: 4000,
    step: 10,
    format: (v) => Math.round(v).toString(),
    hint: "More = denser cover, more cost.",
  },
  {
    key: "maxAge",
    label: "max age (frames)",
    min: 30,
    max: 600,
    step: 5,
    format: (v) => Math.round(v).toString(),
    hint: "Average frames before respawn. Each particle's actual lifespan is ±jitter around this.",
  },
  {
    key: "minPxPerFrame",
    label: "calm-wind floor (px/frame)",
    min: 0,
    max: 1.5,
    step: 0.01,
    format: (v) => v.toFixed(2),
    hint: "Min motion when wind is light. 0 = no floor.",
  },
  {
    key: "fadeInFrames",
    label: "fade-in (frames)",
    min: 0,
    max: 200,
    step: 1,
    format: (v) => Math.round(v).toString(),
    hint: "Frames over which a new particle ramps invisible → full alpha (cosine S-curve). If fadeIn+fadeOut exceed a particle's lifespan, both scale down proportionally.",
  },
  {
    key: "fadeOutFrames",
    label: "fade-out (frames)",
    min: 0,
    max: 200,
    step: 1,
    format: (v) => Math.round(v).toString(),
    hint: "Frames over which a dying particle ramps full → invisible before respawn (cosine S-curve).",
  },
  {
    key: "lifespanJitter",
    label: "lifespan jitter (±%)",
    min: 0,
    max: 0.9,
    step: 0.01,
    format: (v) => `${Math.round(v * 100)}%`,
    hint: "Per-particle lifespan variation. 0 = all particles cycle in lockstep (fade waves). 0.5 = lifespans range 50–150% of maxAge.",
  },
];

// ─── Colour helpers ──────────────────────────────────────────────────────────

function rgbaToHex(rgba: string): string {
  const m = rgba.match(/\d+(?:\.\d+)?/g);
  if (!m || m.length < 3) return "#ffffff";
  const [r, g, b] = m.map(Number) as [number, number, number];
  const h = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
}

function rgbaAlpha(rgba: string): number {
  const m = rgba.match(/\d+(?:\.\d+)?/g);
  if (!m || m.length < 4) return 1;
  return Number(m[3]);
}

function hexToRgba(hex: string, alpha: number): string {
  const v = hex.replace("#", "");
  const full = v.length === 3 ? v.split("").map((c) => c + c).join("") : v;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export function ParticlesTunePage() {
  const [, setLocation] = useLocation();
  const [config, setConfig] = useState<ParticleConfig>(DEFAULT_PARTICLE_CONFIG);
  const [mapStyleId, setMapStyleId] = useState<string>(MAP_STYLE_OPTIONS[0]?.id ?? "");
  const mapStyleUrl =
    MAP_STYLE_OPTIONS.find((s) => s.id === mapStyleId)?.url ?? MAP_STYLE_OPTIONS[0]?.url;

  const updateConfig = <K extends keyof ParticleConfig>(key: K, value: ParticleConfig[K]) => {
    setConfig((c) => ({ ...c, [key]: value }));
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => setLocation("/dev")}>
          ← Dev
        </button>
        <div>
          <div className={styles.title}>Particles tuning</div>
          <div className={styles.subtitle}>
            6 mock scenarios · 16:10 maps · live config below
          </div>
        </div>
      </div>

      <div className={styles.controls}>
        {SLIDERS.map((s) => {
          const value = config[s.key];
          if (typeof value !== "number") return null;
          return (
            <div key={s.key} className={styles.controlRow}>
              <div className={styles.controlLabel}>
                <span className={styles.controlLabelName}>{s.label}</span>
                <span className={styles.controlValue}>
                  {s.format ? s.format(value) : value}
                </span>
              </div>
              <input
                type="range"
                className={styles.controlSlider}
                min={s.min}
                max={s.max}
                step={s.step}
                value={value}
                onChange={(e) => updateConfig(s.key, Number(e.target.value))}
              />
              {s.hint && <span className={styles.controlHint}>{s.hint}</span>}
            </div>
          );
        })}

        {/* Colour controls */}
        <div className={styles.controlRow}>
          <div className={styles.controlLabel}>
            <span className={styles.controlLabelName}>colour</span>
            <span className={styles.controlValue}>{config.color}</span>
          </div>
          <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}>
            <input
              type="color"
              value={rgbaToHex(config.color)}
              onChange={(e) => {
                const alpha = rgbaAlpha(config.color);
                updateConfig("color", hexToRgba(e.target.value, alpha));
              }}
              style={{ width: 44, height: 32, border: "none", background: "transparent" }}
            />
            <input
              type="text"
              value={rgbaToHex(config.color)}
              onChange={(e) => {
                const alpha = rgbaAlpha(config.color);
                if (/^#[0-9a-fA-F]{6}$/.test(e.target.value)) {
                  updateConfig("color", hexToRgba(e.target.value, alpha));
                }
              }}
              style={{
                width: 86,
                height: 32,
                padding: "0 8px",
                background: "transparent",
                border: "1px solid var(--border-default)",
                borderRadius: "var(--radius-sm)",
                color: "var(--foreground-default)",
                fontFamily: "var(--font-body)",
                fontSize: "var(--text-caption)",
              }}
            />
          </div>
          <span className={styles.controlHint}>Hex picker. Particle stroke colour.</span>
        </div>

        <div className={styles.controlRow}>
          <div className={styles.controlLabel}>
            <span className={styles.controlLabelName}>colour alpha</span>
            <span className={styles.controlValue}>{rgbaAlpha(config.color).toFixed(2)}</span>
          </div>
          <input
            type="range"
            className={styles.controlSlider}
            min={0}
            max={1}
            step={0.01}
            value={rgbaAlpha(config.color)}
            onChange={(e) => {
              const hex = rgbaToHex(config.color);
              updateConfig("color", hexToRgba(hex, Number(e.target.value)));
            }}
          />
          <span className={styles.controlHint}>
            Per-stroke opacity. Combined with `trail fade` for total intensity.
          </span>
        </div>

        {/* Line cap selector — controls particle head/tail shape */}
        <div className={styles.controlRow}>
          <div className={styles.controlLabel}>
            <span className={styles.controlLabelName}>line cap (shape)</span>
            <span className={styles.controlValue}>{config.lineCap}</span>
          </div>
          <select
            value={config.lineCap}
            onChange={(e) => updateConfig("lineCap", e.target.value as CanvasLineCap)}
            style={{
              width: "100%",
              height: 32,
              padding: "0 8px",
              background: "transparent",
              border: "1px solid var(--border-default)",
              borderRadius: "var(--radius-sm)",
              color: "var(--foreground-default)",
              fontFamily: "var(--font-body)",
              fontSize: "var(--text-caption)",
            }}
          >
            <option value="round">round (soft head + tail)</option>
            <option value="butt">butt (sharp / arrow-like)</option>
            <option value="square">square (chunky head)</option>
          </select>
          <span className={styles.controlHint}>
            How the line ends are rendered. With tail alpha = 0, "round" gives a comet head; "butt" gives a sharp leading edge.
          </span>
        </div>

        {/* Map style switcher */}
        <div className={styles.controlRow}>
          <div className={styles.controlLabel}>
            <span className={styles.controlLabelName}>map style</span>
            <span className={styles.controlValue}>{mapStyleId}</span>
          </div>
          <select
            value={mapStyleId}
            onChange={(e) => setMapStyleId(e.target.value)}
            style={{
              width: "100%",
              height: 32,
              padding: "0 8px",
              background: "transparent",
              border: "1px solid var(--border-default)",
              borderRadius: "var(--radius-sm)",
              color: "var(--foreground-default)",
              fontFamily: "var(--font-body)",
              fontSize: "var(--text-caption)",
            }}
          >
            {MAP_STYLE_OPTIONS.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
          <span className={styles.controlHint}>
            CARTO basemaps are free / no key. MapTiler entries appear when
            VITE_MAPTILER_KEY is set in `.env.local`.
          </span>
        </div>

        <div className={styles.controlRow}>
          <div className={styles.resetRow}>
            <button
              type="button"
              className={styles.resetButton}
              onClick={() => setConfig(DEFAULT_PARTICLE_CONFIG)}
            >
              Reset to defaults
            </button>
          </div>
          <span className={styles.controlHint}>
            Defaults: {Object.entries(DEFAULT_PARTICLE_CONFIG)
              .map(([k, v]) => `${k}=${v}`)
              .join(", ")}
          </span>
        </div>
      </div>

      <div className={styles.list}>
        {SCENARIOS.map((s) => (
          <div key={s.label} className={styles.card}>
            <div className={styles.cardHeader}>
              <span className={styles.cardTitle}>{s.label}</span>
              <span className={styles.cardWind}>
                {(s.speedMs * 1.944).toFixed(1)} kn ·{" "}
                {degreesToCompass(s.dirDeg)} ({s.dirDeg}°)
              </span>
            </div>
            <div className={styles.mapFrame}>
              <SpotDetailMap
                coords={s.coords}
                windDirectionDeg={s.dirDeg}
                windSpeedMs={s.speedMs}
                zoom={s.zoom}
                particleConfig={config}
                {...(mapStyleUrl !== undefined && { mapStyle: mapStyleUrl })}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
